"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { InputField } from "@/components/form/InputField";

export default function RegisterPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const from = searchParams.get("from") || "/editor";
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ phone?: string; code?: string; password?: string; confirmPassword?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const validatePhone = () => {
    const newErrors: { phone?: string } = {};
    if (!phone) {
      newErrors.phone = "请输入手机号";
    } else if (!/^1[3-9]\d{9}$/.test(phone)) {
      newErrors.phone = "请输入有效的手机号";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const newErrors: { phone?: string; code?: string; password?: string; confirmPassword?: string } = {};
    if (!phone) {
      newErrors.phone = "请输入手机号";
    } else if (!/^1[3-9]\d{9}$/.test(phone)) {
      newErrors.phone = "请输入有效的手机号";
    }
    if (!code) {
      newErrors.code = "请输入验证码";
    } else if (code.length !== 6) {
      newErrors.code = "验证码应为6位数字";
    }
    if (password && password.length < 6) {
      newErrors.password = "密码至少6位";
    }
    if (password && password !== confirmPassword) {
      newErrors.confirmPassword = "两次输入的密码不一致";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendCode = async () => {
    if (!validatePhone()) return;

    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/phone-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();

      if (data.success) {
        setCodeSent(true);
        if (data.code) { setCode(data.code); console.log('[DEV] 验证码:', data.code); }
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setErrors({ phone: data.error || "发送验证码失败" });
      }
    } catch (error) {
      setErrors({ phone: "网络错误，请重试" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const body: { phone: string; code: string; password?: string } = { phone, code };
      if (password) {
        body.password = password;
      }

      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (data.success) {
        router.push(from);
        router.refresh();
      } else {
        setErrors({ code: data.error || "注册失败" });
      }
    } catch (error) {
      setErrors({ code: "网络错误，请重试" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-100 dark:from-blue-950 dark:to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">创建账号</CardTitle>
          <CardDescription>注册 D8D 云端 AI 开发平台账号</CardDescription>
        </CardHeader>
        <form onSubmit={handleRegister}>
          <CardContent className="space-y-4">
            <InputField
              label="手机号"
              name="phone"
              type="tel"
              placeholder="请输入手机号"
              value={phone}
              onChange={setPhone}
              error={errors.phone}
              required
              disabled={isLoading}
            />

            <div className="flex gap-3">
              <div className="flex-1">
                <InputField
                  label="验证码"
                  name="code"
                  type="text"
                  placeholder="6位数字"
                  value={code}
                  onChange={setCode}
                  error={errors.code}
                  required
                  disabled={isLoading || !codeSent}
                  maxLength={6}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendCode}
                  disabled={isLoading || countdown > 0}
                  className="w-[120px]"
                >
                  {countdown > 0 ? `${countdown}s后重发` : codeSent ? "重新发送" : "获取验证码"}
                </Button>
              </div>
            </div>

            <InputField
              label="密码（可选）"
              name="password"
              type="password"
              placeholder="至少6位，不填则仅用验证码登录"
              value={password}
              onChange={setPassword}
              error={errors.password}
              disabled={isLoading}
            />

            <InputField
              label="确认密码"
              name="confirmPassword"
              type="password"
              placeholder="再次输入密码"
              value={confirmPassword}
              onChange={setConfirmPassword}
              error={errors.confirmPassword}
              disabled={isLoading || !password}
            />
          </CardContent>
          <CardFooter className="flex-col gap-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "注册中..." : "注册并登录"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              已有账号？{" "}
              <Link href="/login" className="text-primary hover:underline">
                立即登录
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
